// Copyright (c) 2025 Nebularis Holdings LLC
// Licensed to Nutrx Solutions under a non-exclusive, non-transferable license.
// See IP License Agreement for usage restrictions.
// frontend/src/components/learning/RecommendationQualityCard.jsx

import React from "react";
import EffectivenessBadge from "./EffectivenessBadge";

function clean(value) {
  const text = String(value ?? "").trim();
  return text || "";
}

function humanize(value, fallback = "—") {
  const raw = clean(value);
  if (!raw) return fallback;

  const normalized = raw.replace(/_/g, " ").replace(/\s+/g, " ").trim();
  const lower = normalized.toLowerCase();

  const exactLabels = {
    strong: "Strong",
    moderate: "Moderate",
    improving: "Improving",
    accurate: "Accurate",
    slightly_underestimated: "Slightly underestimated",
    "slightly underestimated": "Slightly underestimated",
    slightly_overestimated: "Slightly overestimated",
    "slightly overestimated": "Slightly overestimated",
    underestimated: "Underestimated",
    overestimated: "Overestimated",
    needs_review: "Needs review",
    "needs review": "Needs review",
  };

  if (exactLabels[lower]) return exactLabels[lower];

  return normalized
    .split(" ")
    .map((word, index) => {
      const key = word.toLowerCase();
      if (exactLabels[key]) return index === 0 ? exactLabels[key] : exactLabels[key].toLowerCase();
      return index === 0
        ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase()
        : word.toLowerCase();
    })
    .join(" ");
}

function formatPercent(value, fallback = "—") {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return `${Math.round(parsed)}%`;
}

function effortLabel(execution = {}) {
  const textValue = clean(execution?.estimated_effort_accuracy);
  if (textValue) return humanize(textValue);

  const rate = execution?.effort_estimate_accuracy_rate;
  if (rate !== null && rate !== undefined && rate !== "") {
    return `${formatPercent(rate)} accurate`;
  }

  return "—";
}

export default function RecommendationQualityCard({ recommendation = {}, execution = {}, validation = {} }) {
  const qualitySignal = humanize(recommendation?.quality_signal || execution?.quality_signal || "measured");
  const orderFollowed = execution?.suggested_order_followed === false ? "No" : "Yes";
  const prerequisites = execution?.prerequisites_correct === false ? "Needs review" : "Correct";

  return (
    <section className="learning-card recommendation-quality-card">
      <div className="learning-card-header">
        <div>
          <p className="learning-eyebrow">Recommendation Quality</p>
          <h4>Effectiveness Signals</h4>
        </div>
        <span className="learning-status-pill learning-status-pill-active">
          {qualitySignal}
        </span>
      </div>

      <div className="learning-badge-grid">
        <EffectivenessBadge label="Accepted" value={recommendation?.accepted_rate ?? (recommendation?.accepted ? 100 : 0)} suffix="percent" signal={recommendation?.quality_signal} compact />
        <EffectivenessBadge label="Completed" value={recommendation?.completed_rate ?? (recommendation?.completed ? 100 : 0)} suffix="percent" signal={recommendation?.quality_signal} compact />
        <EffectivenessBadge label="Validated" value={recommendation?.validated_rate ?? validation?.validation_success_rate ?? 0} suffix="percent" signal={recommendation?.quality_signal} compact />
        <EffectivenessBadge label="Rollback" value={execution?.rollback_rate ?? execution?.rollback_frequency ?? 0} suffix="percent" signal={execution?.quality_signal} compact />
      </div>

      <div className="learning-section-block">
        <h5>Execution plan learning</h5>
        <div className="learning-chip-row">
          <span className="learning-chip">Order followed: {orderFollowed}</span>
          <span className="learning-chip">Prerequisites: {prerequisites}</span>
          <span className="learning-chip">Effort: {effortLabel(execution)}</span>
        </div>
      </div>
    </section>
  );
}
